import { EDeliverableStatus } from '@constants';
import { Button, Drawer, Flex, Input, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '../ErrorBoundary';
import { PdfViewer } from '../PdfViewer';

import { DeliverableScore } from './components/DeliverableScore/DeliverableScore';
import {
  Wrapper,
  TitleWrapper,
  CommentTextWrapper,
  CommentTitleWrapper
} from './DeliverableViewer.styles';

import { DeliverableStatusBadge } from '@/components/DeliverableStatusBadge';
import { DELIVERABLE_MODAL_WIDTH } from '@/constants';
import { useDeliverableViewer, useIsMobile } from '@/hooks';
import { useDeliverableDetail, useMutateDeliverables, useProfile } from '@/store';

const PDF_VIEWER_HEIGHT_OFFSET = 150;

export const DeliverableViewer = () => {
  const { updateDeliverable } = useMutateDeliverables();
  const { deliverableId, setDeliverableId } = useDeliverableViewer();
  const { t } = useTranslation('DeliverableViewerTranslations');
  const { deliverable } = useDeliverableDetail(deliverableId);
  const { profile, roleCheck } = useProfile();
  const isMobile = useIsMobile();
  const [comment, setComment] = useState<string>();

  useEffect(() => {
    if (deliverable) setComment(deliverable.teacherComment);
  }, [deliverable]);

  if (!profile || !deliverable) return null;

  const { isAdmin, isManager, isTeacher, isStudent } = roleCheck;
  const isEditable = isAdmin || isManager || isTeacher;

  const { status } = deliverable;

  const handleChangeStatus = (newStatus: EDeliverableStatus) => {
    if (status === newStatus) return;
    updateDeliverable(
      {
        _id: deliverable._id,
        status: newStatus
      },
      {
        onSuccess: () => {
          if (newStatus === EDeliverableStatus.COMPLETED) setDeliverableId(null);
        }
      }
    );
  };

  const handleClose = () => setDeliverableId(null);

  const handleSetTeacherComment = () =>
    updateDeliverable({ _id: deliverable._id, teacherComment: comment });

  const isPdfFile = deliverable.file.name.includes('.pdf');

  const handleDownloadFile = () => {
    fetch(deliverable.file.url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', deliverable.file.name || deliverable._id);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      });
  };

  return (
    <Drawer
      closable={false}
      onClose={handleClose}
      open={Boolean(deliverableId)}
      width={isMobile ? '100%' : DELIVERABLE_MODAL_WIDTH}
      footer={
        <Button size="small" onClick={handleClose}>
          {t('CLOSE_DELIVERABLE_PREVIEW')}
        </Button>
      }
      title={
        <TitleWrapper>
          <DeliverableStatusBadge
            status={status}
            isEditable={isEditable}
            onChange={handleChangeStatus}
          />
          <DeliverableScore deliverable={deliverable} isEditable={isEditable} />
        </TitleWrapper>
      }>
      <Wrapper vertical gap={32}>
        {isEditable ? (
          <Flex vertical gap={8}>
            <Input.TextArea
              placeholder={t('TEACHER_COMMENT_PLACEHOLDER')}
              value={comment}
              disabled={isStudent}
              onChange={e => setComment(e.target.value)}
            />
            <Flex justify="flex-end">
              <Button type="primary" onClick={handleSetTeacherComment}>
                {t('SAVE_TEACHER_COMMENT')}
              </Button>
            </Flex>
          </Flex>
        ) : (
          comment && (
            <Flex gap={8}>
              <CommentTitleWrapper>{t('TEACHER_COMMENT_TITLE')}</CommentTitleWrapper>
              <CommentTextWrapper>{comment}</CommentTextWrapper>
            </Flex>
          )
        )}
        <ErrorBoundary>
          {isPdfFile ? (
            <PdfViewer
              url={deliverable.file.url}
              height={window.innerHeight - PDF_VIEWER_HEIGHT_OFFSET}
              name={deliverable.file?.name}
            />
          ) : (
            <div>
              <Tag>{deliverable.file.name}</Tag>
              <div style={{ marginTop: 4 }}>
                <Button type="primary" onClick={handleDownloadFile}>
                  {t('DOWNLOAD_FILE')}
                </Button>
              </div>
            </div>
          )}
        </ErrorBoundary>
      </Wrapper>
    </Drawer>
  );
};
